import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiPlay, FiPause, FiSkipBack, FiSkipForward, FiVolume2 } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useAudio } from '../context/AudioContext';

const AudioPlayer = () => {
  const { 
    currentEpisode, 
    isPlaying, 
    progress, 
    togglePlay, 
    seek, 
    duration, 
    playNext, 
    playPrev,
    hasNext,
    hasPrev 
  } = useAudio();

  const formatTime = (seconds) => {
    if (!seconds) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <AnimatePresence>
      {currentEpisode && (
        <motion.div
          initial={{ y: 100 }}
          animate={{ y: 0 }}
          exit={{ y: 100 }}
          // Increased z-index to 120 to ensure it sits ABOVE the modal (z-100)
          className="fixed bottom-0 left-0 right-0 z-[120] bg-brand-dark text-white border-t border-brand-tan/20 shadow-2xl px-4 py-3 md:py-4"
        >
          <div className="max-w-7xl mx-auto flex items-center gap-4 md:gap-8">
            {/* Episode Info (Compact) */}
            <div className="flex items-center gap-3 min-w-0 flex-1 md:flex-none md:w-1/3">
              <img 
                src={currentEpisode.image} 
                alt="" 
                className="w-10 h-10 md:w-12 md:h-12 rounded bg-brand-green object-cover border border-white/10" 
              />
              <div className="min-w-0">
                <h4 className="text-sm font-bold truncate font-serif">{currentEpisode.title}</h4>
                <p className="text-[10px] text-brand-tan font-bold uppercase tracking-wider truncate">
                  {currentEpisode.pubDate}
                </p>
              </div>
            </div>

            {/* Controls & Progress */}
            <div className="flex-1 flex flex-col gap-1">
              <div className="flex items-center justify-center gap-4 md:gap-6">
                <button 
                  onClick={playPrev}
                  disabled={!hasPrev}
                  className={`text-white/60 hover:text-brand-tan transition-colors ${!hasPrev ? 'opacity-30 cursor-not-allowed' : ''}`}
                >
                  <SafeIcon icon={FiSkipBack} className="w-5 h-5" />
                </button>
                
                <button 
                  onClick={togglePlay}
                  className="bg-brand-tan text-white p-2 md:p-3 rounded-full hover:scale-105 transition-transform shadow-lg"
                >
                  <SafeIcon icon={isPlaying ? FiPause : FiPlay} className="w-5 h-5 fill-current" />
                </button>

                <button 
                  onClick={playNext}
                  disabled={!hasNext}
                  className={`text-white/60 hover:text-brand-tan transition-colors ${!hasNext ? 'opacity-30 cursor-not-allowed' : ''}`}
                >
                  <SafeIcon icon={FiSkipForward} className="w-5 h-5" />
                </button>
              </div>

              <div className="flex items-center gap-3">
                <span className="text-[10px] font-mono opacity-60 w-8 text-right">
                  {formatTime((progress / 100) * (duration || 0))}
                </span>
                <div 
                  className="flex-1 h-1.5 bg-white/10 rounded-full relative overflow-hidden group cursor-pointer"
                  onClick={(e) => {
                    const rect = e.currentTarget.getBoundingClientRect();
                    const x = e.clientX - rect.left;
                    seek((x / rect.width) * 100);
                  }}
                >
                  <div 
                    className="absolute top-0 left-0 h-full bg-brand-tan transition-all duration-100" 
                    style={{ width: `${progress}%` }} 
                  />
                </div>
                <span className="text-[10px] font-mono opacity-60 w-8">
                  {formatTime(duration)}
                </span>
              </div>
            </div>

            {/* Right Side Options */}
            <div className="hidden md:flex items-center gap-4 w-1/3 justify-end">
              <div className="flex items-center gap-2 group">
                <SafeIcon icon={FiVolume2} className="text-white/60 group-hover:text-brand-tan" />
                <div className="w-20 h-1 bg-white/10 rounded-full">
                  <div className="w-2/3 h-full bg-white/40 rounded-full" />
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AudioPlayer;